import { FilterBar, FilterType } from '@flik/shared';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import Badge from '../../components/Badge';
import { useIdentityContext } from '../../components/IdentityProvider';
// import ResultQuickActionMenu, {
//   ResultQuickActionMenuRef,
// } from '../../components/ResultQuickActionMenu';
import { useTheme } from '@flik/shared';
import { useSessionState } from '../../components/SessionStateProvider';
import SignIn from '../../components/SignIn';
import { StartFlikButton } from '../../components/StartFlikButton';
import { ContentProps } from '../types';
import {
  initializeDefaultProviders,
  removeRecentEmail,
  searchProviderRegistry,
  SearchResultItem,
} from './providers';
import { useEnrichedSearchContextWithSessions } from './providers/session-provider';

// Initialize providers immediately when this module is imported
initializeDefaultProviders();

interface SearchContentProps
  extends Omit<
    ContentProps,
    | 'showSignIn'
    | 'onClearSearch'
    | 'onAddBadge'
    | 'onRemoveBadge'
    | 'onClearBadges'
    | 'onNavigateToAction'
  > {
  showSignIn?: boolean;
  onClearSearch?: () => void;
  onAddBadge?: (badge: string) => void;
  onRemoveBadge?: (badge: string) => void;
  onClearBadges?: () => void;
  onNavigateToAction?: (actionKey: string) => void;
  onUpdateHasResults?: (hasResults: boolean) => void;
}

// Use SearchResultItem from providers instead of local interface
type SearchResult = SearchResultItem;

export interface SearchContentRef {
  navigateDown: () => void;
  navigateUp: () => void;
  selectCurrent: () => void;
}

const SearchContent = forwardRef<SearchContentRef, SearchContentProps>(
  (
    {
      searchQuery,
      showSignIn,
      // requestWindowResize, // Not used in web app
      onClearSearch,
      badges = [],
      onRemoveBadge,
      onClearBadges,
      onAddBadge,
      selectedResultIndex = -1,
      setSelectedResultIndex,
      // onKeyDown, // Not used in web app
      onNavigateToAction,
      onUpdateHasResults,
    },
    ref,
  ) => {
    const { isLoggedIn, isLoading } = useIdentityContext();
    const sessionHook = useSessionState();
    const { hasSession } = sessionHook;
    const { theme } = useTheme();
    const [activeFilter, setActiveFilter] = useState<FilterType>('all');
    const [isKeyboardNavigating, setIsKeyboardNavigating] = useState(false);
    // const [lastKeyboardNavigationTime, setLastKeyboardNavigationTime] =
    //   useState(Date.now());
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    // const quickActionMenuRefs = useRef<Map<string, ResultQuickActionMenuRef>>(
    //   new Map(),
    // );

    // Build base context
    const baseContext = {
      searchQuery,
      badges,
      hasSession,
      isLoggedIn,
      isLoading,
      onAddBadge,
      onRemoveBadge,
      onClearBadges,
      onClearSearch,
      onNavigateToAction,
      signOut: undefined, // TODO: Add to ActionContext if needed
      createSession: undefined, // TODO: Add to ActionContext if needed
      endSession: undefined, // TODO: Add to ActionContext if needed
      sessionHook,
      removeRecentEmail: (email: string) => {
        removeRecentEmail(email);
        setRefreshTrigger((prev) => prev + 1);
      },
      refreshTrigger,
    };

    // Enrich context with provider-specific data (e.g., session data)
    const enrichedContext = useEnrichedSearchContextWithSessions(baseContext);

    // Get search results from providers
    const searchResults = useMemo(() => {
      return searchProviderRegistry.getResults(enrichedContext);
    }, [
      searchQuery,
      badges,
      hasSession,
      isLoggedIn,
      isLoading,
      onAddBadge,
      onRemoveBadge,
      onClearBadges,
      onClearSearch,
      onNavigateToAction,
      enrichedContext,
    ]);

    // First, filter results by search query and badges (AND operation)
    const queryFilteredResults = searchResults.filter((result) => {
      // Filter by search query
      // Skip query filtering for items that have skipQueryFiltering set to true
      // (e.g., server-side filtered results like sessions search)
      const matchesSearchQuery =
        result.skipQueryFiltering ||
        !searchQuery ||
        result.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (result.subtitle &&
          result.subtitle.toLowerCase().includes(searchQuery.toLowerCase()));

      // Filter by badges (AND operation - result must match ALL badges)
      // Skip badge filtering for items that have skipBadgeFiltering set to true
      const matchesBadges =
        badges.length === 0 ||
        result.skipBadgeFiltering ||
        badges.every((badge) => {
          const badgeLower = badge.toLowerCase();
          return (
            result.title.toLowerCase().includes(badgeLower) ||
            (result.subtitle &&
              result.subtitle.toLowerCase().includes(badgeLower))
          );
        });

      return matchesSearchQuery && matchesBadges;
    });

    // Update parent's hasResults state when search results change
    useEffect(() => {
      if (onUpdateHasResults) {
        const hasActualResults = queryFilteredResults.length > 0;
        onUpdateHasResults(hasActualResults);
      }
    }, [queryFilteredResults.length, onUpdateHasResults]);

    // Then filter by active filter
    const filteredResults = queryFilteredResults.filter((result) => {
      return activeFilter === 'all' || result.type === activeFilter;
    });

    // Group results by type for display
    const groupedResults = filteredResults.reduce((groups, result) => {
      if (!groups[result.type]) {
        groups[result.type] = [];
      }
      groups[result.type].push(result);
      return groups;
    }, {} as Record<string, SearchResult[]>);

    // Define group order and display names - only show types that have results after query filtering
    const availableTypes = Object.keys(groupedResults).filter(
      (type) => groupedResults[type].length > 0,
    );

    const groupOrder = availableTypes.map((type) => {
      const labelMap: Record<string, string> = {
        system: 'System',
        action: 'Actions',
        email: 'Recent Emails',
        content: 'Content',
        session: 'Sessions',
        settings: 'Settings',
        file: 'Files',
        view: 'Views',
      };

      return {
        key: type,
        label: labelMap[type] || type.charAt(0).toUpperCase() + type.slice(1),
      };
    });

    // Function to scroll selected item into view
    const scrollSelectedItemIntoView = (index: number) => {
      if (!scrollContainerRef.current) return;

      const container = scrollContainerRef.current;
      const selectedElement = container.querySelector(
        `[data-result-index="${index}"]`,
      );
      if (selectedElement) {
        selectedElement.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
        });
      }
    };

    // Clear keyboard navigation flag when mouse leaves the results area
    const handleMouseLeaveResults = () => {
      setIsKeyboardNavigating(false);
    };

    // Handle mouse movement to detect when user starts using mouse again
    const handleMouseMove = () => {
      setIsKeyboardNavigating(false);
    };

    // Handle result click
    const handleResultClick = useCallback(
      (result: SearchResult) => {
        // Open external URL if specified (for web, use window.open)
        if (result.externalUrl) {
          window.open(result.externalUrl, '_self', 'noopener,noreferrer');
          return;
        }

        // Navigate to the action if actionKey is specified
        if (result.actionKey && onNavigateToAction) {
          onNavigateToAction(result.actionKey);
        }

        // Add as badge if it's a badgeable item
        if (result.canAddAsBadge && result.badgeValue && onAddBadge) {
          onAddBadge(result.badgeValue);
        }
      },
      [onNavigateToAction, onAddBadge],
    );

    // Expose navigation functions to parent
    useImperativeHandle(
      ref,
      () => ({
        navigateDown: () => {
          if (!setSelectedResultIndex) return;

          const allResults = filteredResults;
          const totalItems = allResults.length;
          if (totalItems === 0) return;

          setIsKeyboardNavigating(true);
          // setLastKeyboardNavigationTime(Date.now());

          if (selectedResultIndex === -1) {
            setSelectedResultIndex(0);
            setTimeout(() => scrollSelectedItemIntoView(0), 0);
            return;
          }

          const newIndex =
            selectedResultIndex < totalItems - 1 ? selectedResultIndex + 1 : 0;
          setSelectedResultIndex(newIndex);
          setTimeout(() => scrollSelectedItemIntoView(newIndex), 0);
        },
        navigateUp: () => {
          if (!setSelectedResultIndex) return;

          const allResults = filteredResults;
          const totalItems = allResults.length;
          if (totalItems === 0) return;

          setIsKeyboardNavigating(true);
          // setLastKeyboardNavigationTime(Date.now());

          if (selectedResultIndex === -1) {
            setSelectedResultIndex(totalItems - 1);
            setTimeout(() => scrollSelectedItemIntoView(totalItems - 1), 0);
            return;
          }

          const newIndex =
            selectedResultIndex > 0 ? selectedResultIndex - 1 : totalItems - 1;
          setSelectedResultIndex(newIndex);
          setTimeout(() => scrollSelectedItemIntoView(newIndex), 0);
        },
        selectCurrent: () => {
          if (!setSelectedResultIndex) return;

          const allResults = filteredResults;
          if (
            selectedResultIndex >= 0 &&
            selectedResultIndex < allResults.length
          ) {
            const selectedResult = allResults[selectedResultIndex];
            if (selectedResult) {
              handleResultClick(selectedResult);
            }
          }
        },
      }),
      [
        selectedResultIndex,
        filteredResults,
        setSelectedResultIndex,
        handleResultClick,
      ],
    );

    // Handle badge removal
    const handleRemoveBadge = useCallback(
      (badgeToRemove: string) => {
        if (onRemoveBadge) {
          onRemoveBadge(badgeToRemove);
        }
      },
      [onRemoveBadge],
    );

    // Handle badge clearing
    // const handleClearBadges = useCallback(() => {
    //   if (onClearBadges) {
    //     onClearBadges();
    //   }
    // }, [onClearBadges]);

    // Check if all badges are valid emails
    const allBadgesAreEmails = useMemo(() => {
      return (
        badges.length > 0 &&
        badges.every((badge) => {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          return emailRegex.test(badge.trim());
        })
      );
    }, [badges]);

    // Show sign-in form if not logged in
    if (!isLoggedIn) {
      return (
        <div className="flex-1 flex flex-col p-4">
          <SignIn />
        </div>
      );
    }

    // Show sign-in form if explicitly requested
    if (showSignIn) {
      return (
        <div className="flex-1 flex flex-col p-4">
          <SignIn />
        </div>
      );
    }

    return (
      <>
        {/* Badge Display - positioned at top like v1 */}
        {badges.length > 0 && (
          <div className="mb-3">
            <div className="flex items-center space-x-2">
              <div className="flex-1 overflow-x-auto scrollbar-none pr-2">
                <div className="flex space-x-1.5 min-w-max">
                  {badges.map((badge, index) => (
                    <Badge
                      key={index}
                      onRemove={() => handleRemoveBadge(badge)}
                      clearable
                    >
                      {badge}
                    </Badge>
                  ))}
                </div>
              </div>
              {/* Start Flik Button - only show when not in session and have valid email badges */}
              {!hasSession && allBadgesAreEmails && (
                <StartFlikButton emails={badges} onSuccess={onClearBadges} />
              )}
            </div>
          </div>
        )}

        {/* Content Area */}
        <div
          ref={scrollContainerRef}
          className="flex-1 max-h-80 overflow-y-auto scrollbar-thin scrollbar-track-transparent scrollbar-thumb-gray-400/30 scrollbar-thumb-rounded hover:scrollbar-thumb-gray-400/50 px-2 focus:outline-none"
          onMouseLeave={handleMouseLeaveResults}
          onMouseMove={handleMouseMove}
          onClick={() => {
            // Focus the content area when clicked so it can receive keyboard events
            if (scrollContainerRef.current) {
              scrollContainerRef.current.focus();
            }
          }}
          tabIndex={0}
          onKeyDown={(e) => {
            // Handle arrow keys when SearchContent is focused
            if (
              e.key === 'ArrowDown' ||
              e.key === 'ArrowUp' ||
              e.key === 'Enter'
            ) {
              e.preventDefault();
              e.stopPropagation();

              if (e.key === 'ArrowDown') {
                if (setSelectedResultIndex) {
                  const allResults = filteredResults;
                  const totalItems = allResults.length;
                  if (totalItems === 0) return;

                  setIsKeyboardNavigating(true);
                  // setLastKeyboardNavigationTime(Date.now());

                  if (selectedResultIndex === -1) {
                    setSelectedResultIndex(0);
                    setTimeout(() => scrollSelectedItemIntoView(0), 0);
                    return;
                  }

                  const newIndex =
                    selectedResultIndex < totalItems - 1
                      ? selectedResultIndex + 1
                      : 0;
                  setSelectedResultIndex(newIndex);
                  setTimeout(() => scrollSelectedItemIntoView(newIndex), 0);
                }
              } else if (e.key === 'ArrowUp') {
                if (setSelectedResultIndex) {
                  const allResults = filteredResults;
                  const totalItems = allResults.length;
                  if (totalItems === 0) return;

                  setIsKeyboardNavigating(true);
                  // setLastKeyboardNavigationTime(Date.now());

                  if (selectedResultIndex === -1) {
                    setSelectedResultIndex(totalItems - 1);
                    setTimeout(
                      () => scrollSelectedItemIntoView(totalItems - 1),
                      0,
                    );
                    return;
                  }

                  const newIndex =
                    selectedResultIndex > 0
                      ? selectedResultIndex - 1
                      : totalItems - 1;
                  setSelectedResultIndex(newIndex);
                  setTimeout(() => scrollSelectedItemIntoView(newIndex), 0);
                }
              } else if (e.key === 'Enter') {
                const allResults = filteredResults;
                if (
                  selectedResultIndex >= 0 &&
                  selectedResultIndex < allResults.length
                ) {
                  const selectedResult = allResults[selectedResultIndex];
                  if (selectedResult) {
                    handleResultClick(selectedResult);
                  }
                }
              }
            }
          }}
        >
          {/* Filter Bar - positioned after badges like v1 */}
          {queryFilteredResults.length > 0 && (
            <FilterBar
              activeFilter={activeFilter}
              onFilterChange={setActiveFilter}
              hasResults={queryFilteredResults.length > 0}
              isComponentActive={false}
              availableFilters={[
                { type: 'all', label: 'All' },
                { type: 'system', label: 'System' },
                { type: 'action', label: 'Action' },
              ]}
            />
          )}
          {filteredResults.length > 0 ? (
            <div>
              {groupOrder.map((group) => {
                const groupItems = groupedResults[group.key] || [];
                if (groupItems.length === 0) return null;

                return (
                  <div key={group.key} className="mb-4">
                    {/* Group Header */}
                    <div className="group-header mb-2">
                      <div
                        className={`text-xs font-semibold tracking-wide ${
                          theme === 'light' ? 'text-gray-600' : 'text-white'
                        }`}
                      >
                        {group.label.charAt(0).toUpperCase() +
                          group.label.slice(1).toLowerCase()}
                      </div>
                      <div
                        className={`h-px mt-1 ${
                          theme === 'light' ? 'bg-gray-300' : 'bg-gray-600'
                        }`}
                      ></div>
                    </div>

                    {/* Group Items */}
                    {groupItems.map((result, index) => {
                      // Calculate truly unique global index across all groups
                      let globalIndex = 0;
                      for (let i = 0; i < groupOrder.length; i++) {
                        const prevGroup = groupOrder[i];
                        if (prevGroup.key === group.key) {
                          globalIndex += index;
                          break;
                        }
                        globalIndex += (groupedResults[prevGroup.key] || [])
                          .length;
                      }

                      return (
                        <div
                          key={result.id}
                          data-result-index={globalIndex}
                          className={`result-item flex items-center p-2 mb-1 rounded cursor-pointer transition-all duration-0 relative group ${
                            globalIndex === selectedResultIndex
                              ? 'shadow-sm selected-item'
                              : ''
                          }`}
                          style={{
                            background:
                              globalIndex === selectedResultIndex
                                ? theme === 'light'
                                  ? `linear-gradient(white, white) padding-box, linear-gradient(to right, #ec4899, #8b5cf6) border-box`
                                  : 'rgba(107, 114, 128, 0.4)' // Brighter grey highlight for dark mode
                                : 'transparent',
                            border:
                              globalIndex === selectedResultIndex
                                ? theme === 'light'
                                  ? '1px solid transparent'
                                  : 'none'
                                : 'none',
                            borderRadius:
                              globalIndex === selectedResultIndex &&
                              theme === 'light'
                                ? '12px'
                                : '8px',
                          }}
                          role="button"
                          tabIndex={0}
                          onMouseEnter={() => {
                            // Completely ignore mouse hover during keyboard navigation
                            // Only allow mouse hover if we're not in keyboard navigation mode
                            if (!isKeyboardNavigating) {
                              if (setSelectedResultIndex) {
                                setSelectedResultIndex(globalIndex);
                              }
                              // Don't auto-scroll on mouse hover - only on keyboard navigation
                            }
                          }}
                          onMouseLeave={() => {
                            // Clear selection when mouse leaves the item, but only if not keyboard navigating
                            if (
                              !isKeyboardNavigating &&
                              setSelectedResultIndex
                            ) {
                              setSelectedResultIndex(-1);
                            }

                            // Close any open quick action menu for this result
                            // const quickActionRef =
                            //   quickActionMenuRefs.current.get(result.id);
                            // if (quickActionRef) {
                            //   quickActionRef.closeMenu();
                            // }
                          }}
                          onClick={() => handleResultClick(result)}
                        >
                          <div
                            className={`result-icon w-6 h-6 rounded mr-2 flex items-center justify-center text-xs ${
                              theme === 'light'
                                ? 'bg-gray-200 text-gray-600'
                                : 'bg-gray-700 text-gray-300'
                            }`}
                          >
                            {result.icon}
                          </div>
                          <div className="result-content flex-1">
                            <div
                              className={`result-title text-sm font-medium ${
                                theme === 'light'
                                  ? 'text-gray-900'
                                  : 'text-white'
                              }`}
                            >
                              {result.title}
                            </div>
                          </div>

                          {/* Quick Action Menu for Result Actions - disabled for web app */}
                          {/* {result.quickActions &&
                            result.quickActions.length > 0 && (
                              <div className="result-actions ml-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                                <ResultQuickActionMenu
                                  ref={(ref) => {
                                    if (ref) {
                                      quickActionMenuRefs.current.set(
                                        result.id,
                                        ref,
                                      );
                                    } else {
                                      quickActionMenuRefs.current.delete(
                                        result.id,
                                      );
                                    }
                                  }}
                                  actions={result.quickActions}
                                />
                              </div>
                            )} */}

                          {/* Tooltip for subtitle */}
                          {result.subtitle && (
                            <div
                              className={`absolute left-0 px-3 py-2 text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-10 max-w-xs pointer-events-none bottom-full mb-2 shadow-lg ${
                                theme === 'light'
                                  ? 'bg-white text-gray-900 border border-gray-200'
                                  : 'bg-gray-900 text-white border border-gray-700'
                              }`}
                            >
                              {result.subtitle}
                              <div
                                className={`absolute w-0 h-0 border-l-4 border-r-4 border-transparent pointer-events-none top-full left-4 border-t-4 ${
                                  theme === 'light'
                                    ? 'border-t-gray-200'
                                    : 'border-t-gray-900'
                                }`}
                              ></div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          ) : searchQuery || badges.length > 0 ? (
            <div className="flex items-center justify-center h-full">
              <div
                className={`text-center ${
                  theme === 'light' ? 'text-gray-900' : 'text-white'
                }`}
              >
                <div className="text-lg font-medium mb-2">No results found</div>
                <div
                  className={`text-sm ${
                    theme === 'light' ? 'text-gray-600' : 'text-gray-300'
                  }`}
                >
                  Try adjusting your search terms or filters
                </div>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-full">
              <div
                className={`text-center ${
                  theme === 'light' ? 'text-gray-900' : 'text-white'
                }`}
              >
                <div className="text-lg font-medium mb-2">Search Content</div>
                <div
                  className={`text-sm ${
                    theme === 'light' ? 'text-gray-600' : 'text-gray-300'
                  }`}
                >
                  Type to search or double-click to see all content
                </div>
              </div>
            </div>
          )}
        </div>
      </>
    );
  },
);

SearchContent.displayName = 'SearchContent';

export { SearchContent };
